import { useMemo, useEffect, type ComponentType, type ReactElement } from 'react';
import { defaultLocale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useUserSubscriber } from '@atlassian/jira-user-services/src/main.tsx';
import type { Controller, ControllerBasic, ControllerFull, Data } from './types.tsx';

const useCurrentUserBasic = (): ControllerBasic => {
	const tenantContext = useTenantContext();

	return useMemo(
		() => ({
			data: {
				user: {
					accountId: tenantContext.atlassianAccountId,
					isAdmin: tenantContext.isAdmin,
					isAnonymous: tenantContext.isAnonymous,
					isSiteAdmin: tenantContext.isSiteAdmin,
					locale: tenantContext.locale || defaultLocale,
					siteAdminStatus: tenantContext.siteAdminStatus,
					userFullname: tenantContext.userFullname || '',
				},
			},
			loading: false,
			error: null,
		}),
		[
			tenantContext.atlassianAccountId,
			tenantContext.isAdmin,
			tenantContext.isAnonymous,
			tenantContext.isSiteAdmin,
			tenantContext.locale,
			tenantContext.siteAdminStatus,
			tenantContext.userFullname,
		],
	);
};

const useCurrentUserFull = (): ControllerFull => {
	const { data: dataBasic } = useCurrentUserBasic();
	const tenantContext = useTenantContext();

	const [{ data, loading, error }, { fetch }] = useUserSubscriber();

	useEffect(() => {
		tenantContext.atlassianAccountId &&
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			fetch({ params: { accountId: tenantContext.atlassianAccountId as string } }, false);
	}, [fetch, tenantContext.atlassianAccountId]);

	return useMemo(
		() => ({
			data: {
				...dataBasic,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				user: {
					...dataBasic.user,
					...data,
				} as Data,
			},
			loading,
			error,
		}),
		[data, dataBasic, error, loading],
	);
};

export const useCurrentUser = (): Controller =>
	useTenantContext().isAnonymous ? useCurrentUserBasic() : useCurrentUserFull(); // eslint-disable-line react-hooks/rules-of-hooks

export const CurrentUserProvider: ComponentType<{
	children: (arg1: Controller) => ReactElement;
}> = ({ children }) => {
	const result = useCurrentUser();
	return children(result);
};
